'use client';

import React from 'react';
import type {
  CustomerAmount,
  CustomerStatusFinancialDetails,
} from '@packages/gql/generated/shopping/graphql';
import { Card, CardContent, CardHeader } from '@packages/shared/src/ui/Card/Card';
import { FormattedMessage } from 'react-intl';
import { Divider } from '@packages/shared/src/ui/Divider/Divider';
import { Price } from '@packages/shared/src/ui/Price/Price';
import { PriceValue } from '@packages/shared/src/ui/Price/PriceValue/PriceValue';
import { useFormatPrice } from '@packages/shared/src/hooks/useFormatPrice/useFormatPrice';
import { AccountBalanceSummary } from '../AccountBalanceSummary/AccountBalanceSummary.client';

interface Props {
  balance: CustomerAmount;
  financialDetails: CustomerStatusFinancialDetails | null;
  className?: string;
  cardHeadline: React.ReactNode;
  cardFooter?: React.ReactNode;
}
export const Maturities = ({
  balance,
  financialDetails,
  className,
  cardHeadline,
  cardFooter,
}: Props) => {
  const { formatPrice } = useFormatPrice();

  return (
    <Card className={className} hasAutoSeparation>
      <CardHeader hasDivider>{cardHeadline}</CardHeader>
      <CardContent className="text-size-body-sm pb-2">
        <AccountBalanceSummary
          label={
            <FormattedMessage id="Maturities.AccountBalance" defaultMessage="Dein Kontostand" />
          }
          balance={balance}
        />
        {financialDetails && (
          <div className="border-color-grey my-1.5 rounded-md border p-1.5">
            <div className="flex justify-between">
              <div className="font-bold">
                <FormattedMessage
                  id="Maturities.Financials.totalMinimumRate"
                  defaultMessage="Fälliger Betrag"
                />
              </div>
              <div className="font-bold">
                <Price size="small">
                  <PriceValue>
                    {formatPrice(financialDetails.totalMinimumRate, balance.currencyCode)}
                  </PriceValue>
                </Price>
              </div>
            </div>
            <Divider className="my-0.5" />
            <div className="flex justify-between">
              <div>
                <FormattedMessage
                  id="Maturities.Financials.minimumRate"
                  defaultMessage="Mindestrate"
                />
              </div>
              <div>
                <Price size="small">
                  <PriceValue className="font-normal">
                    {formatPrice(financialDetails.minimumRate, balance.currencyCode)}
                  </PriceValue>
                </Price>
              </div>
            </div>
            <div className="flex justify-between">
              <div>
                <FormattedMessage
                  id="Maturities.Financials.rdiAmount"
                  defaultMessage="Aktuelle Prämie Zahlschutz"
                />
              </div>
              <div>
                <Price size="small">
                  <PriceValue className="font-normal">
                    {formatPrice(financialDetails.rdiAmount, balance.currencyCode)}
                  </PriceValue>
                </Price>
              </div>
            </div>
          </div>
        )}
      </CardContent>
      {cardFooter}
    </Card>
  );
};
